"use client";

import { useState } from "react";
import { Frame, Slider, Verdict, Note } from "./parts";

/**
 * L'ACRE avant et après le 1er juillet 2026.
 *
 * La réforme divise l'aide par deux, et la bascule ne dépend ni du chiffre
 * d'affaires ni du profil : elle dépend de la DATE de début d'activité. Deux
 * personnes identiques, séparées par une semaine de calendrier, ne paient pas
 * la même chose pendant plus d'un an.
 *
 * C'est le type de règle qu'un paragraphe explique mal et qu'un curseur de date
 * rend évident. C'est aussi, accessoirement, la démonstration que ce glossaire
 * date ses chiffres : la même page écrite début 2026 serait fausse aujourd'hui.
 *
 * Les taux affichés sont les taux PAYÉS avec ACRE, tels que retenus au
 * référentiel arbitré du 05/09/2026, et non des pourcentages d'exonération.
 */

const L = {
    fr: {
        title: "Une semaine d'écart, et l'aide est divisée par deux",
        activity: "Activité",
        vente: "Vente",
        services: "Services (BIC)",
        bnc: "Libéral (BNC)",
        cipav: "CIPAV",
        month: "Mois de début d'activité en 2026",
        ca: "Chiffre d'affaires sur la période aidée",
        withAcre: "Cotisations avec l'ACRE",
        without: "Sans l'ACRE",
        saved: "Économisé",
        before: "Début avant le 1er juillet 2026 : l'exonération est de 50 %.",
        after: "Début à partir du 1er juillet 2026 : l'exonération tombe à 25 %. Le taux payé est désormais de 75 % du taux normal.",
        note: "L'ACRE court jusqu'à la fin du troisième trimestre civil qui suit celui du début d'activité, et sa durée ne dépend pas du chiffre d'affaires réalisé : elle s'épuise même si vous ne facturez rien. Depuis le 1er janvier 2026, elle doit être demandée à l'URSSAF, dans les soixante jours suivant le début d'activité.",
        months: ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"],
    },
    en: {
        title: "One week apart, and the relief is halved",
        activity: "Activity",
        vente: "Goods",
        services: "Services (BIC)",
        bnc: "Liberal (BNC)",
        cipav: "CIPAV",
        month: "Month business starts in 2026",
        ca: "Turnover over the relief period",
        withAcre: "Contributions with ACRE",
        without: "Without ACRE",
        saved: "Saved",
        before: "Starting before 1 July 2026: relief is 50%.",
        after: "Starting on or after 1 July 2026: relief drops to 25%. The rate paid is now 75% of the normal rate.",
        note: "ACRE runs until the end of the third calendar quarter following the one in which the business started, and its duration does not depend on turnover: it runs out even if you invoice nothing. Since 1 January 2026 it must be requested from URSSAF, within sixty days of starting.",
        months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
    },
};

// Taux PAYES avec ACRE, referentiel arbitre du 05/09/2026.
const TAUX = {
    vente: { plein: 0.123, avant: 0.062, apres: 0.093 },
    services: { plein: 0.212, avant: 0.106, apres: 0.159 },
    bnc: { plein: 0.256, avant: 0.128, apres: 0.192 },
    cipav: { plein: 0.232, avant: 0.134, apres: 0.174 },
};

const money = (v, lang) =>
    `${Math.round(v).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} €`;

export default function MeAcreSwitch({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [activite, setActivite] = useState("bnc");
    const [mois, setMois] = useState(5);
    const [ca, setCa] = useState(15000);

    const avantReforme = mois < 6;
    const a = TAUX[activite];
    const tauxPaye = avantReforme ? a.avant : a.apres;

    const avecAcre = ca * tauxPaye;
    const sansAcre = ca * a.plein;
    const economie = sansAcre - avecAcre;

    return (
        <Frame icon="calendar-mark-linear" title={t.title}>
            <div className="flex flex-wrap gap-2 mb-5">
                {["vente", "services", "bnc", "cipav"].map((cle) => (
                    <button
                        key={cle}
                        type="button"
                        onClick={() => setActivite(cle)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            activite === cle
                                ? "bg-primary text-white"
                                : "bg-gray-100 dark:bg-white/[0.06] text-gray-600 dark:text-lightBlue hover:bg-gray-200 dark:hover:bg-white/10"
                        }`}
                    >
                        {t[cle]}
                    </button>
                ))}
            </div>

            <div className="space-y-4">
                <Slider
                    label={t.month}
                    value={mois}
                    onChange={setMois}
                    min={0}
                    max={11}
                    display={t.months[mois]}
                />
                <Slider
                    label={t.ca}
                    value={ca}
                    onChange={setCa}
                    min={2000}
                    max={60000}
                    step={1000}
                    display={money(ca, lang)}
                />
            </div>

            <div className="grid grid-cols-2 gap-3 mt-5">
                <div className="rounded-xl p-4 border border-primary/40 bg-primary/[0.06]">
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">
                        {t.withAcre}
                    </div>
                    <div className="text-xl font-bold tabular-nums dark:text-white">
                        {money(avecAcre, lang)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1 tabular-nums">
                        {(tauxPaye * 100)
                            .toFixed(1)
                            .replace(".", lang === "fr" ? "," : ".")}{" "}
                        %
                    </div>
                </div>
                <div className="rounded-xl p-4 border border-gray-200/70 dark:border-white/10">
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">
                        {t.without}
                    </div>
                    <div className="text-xl font-bold tabular-nums dark:text-white">
                        {money(sansAcre, lang)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1 tabular-nums">
                        {(a.plein * 100)
                            .toFixed(1)
                            .replace(".", lang === "fr" ? "," : ".")}{" "}
                        %
                    </div>
                </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-lightBlue mb-0 mt-4">
                {t.saved} : <b className="dark:text-white">{money(economie, lang)}</b>
            </p>

            <Verdict tone={avantReforme ? "good" : "warn"}>
                {avantReforme ? t.before : t.after}
            </Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
